import { EChartsOption } from "echarts";

import { ChartContext } from "components/ml/types";

import { ChartDecorator } from "./decorator";

export const scatterHighlightDecorator: ChartDecorator = {
  decorate: function (option: EChartsOption, context: ChartContext): EChartsOption {
    const hoveredId = context?.hoveredId;

    if (option.series && Array.isArray(option.series)) {
      option.series = option.series.map((series) => {
        if (series.type === "scatter") {
          const newSeries = { ...series };

          newSeries.emphasis = {
            focus: "self",
            itemStyle: {
              opacity: 1,
              borderColor: "#333",
              borderWidth: 1
            }
          };

          // Add blur configuration for other points when hovering
          newSeries.blur = {
            itemStyle: {
              opacity: 0.15
            }
          };

          // If we have a hoveredId, apply dimming to all non-matching points directly
          if (hoveredId && Array.isArray(newSeries.data)) {
            newSeries.data = newSeries.data.map(
              (dataPoint: { value: (number | string)[]; itemStyle: unknown }) => {
                const isHighlighted = dataPoint.value && dataPoint.value[2] === hoveredId;
                const originalItemStyle =
                  dataPoint.itemStyle && typeof dataPoint.itemStyle === "object"
                    ? dataPoint.itemStyle
                    : {};
                // For non-highlighted points, explicitly reduce opacity
                if (!isHighlighted) {
                  return {
                    ...dataPoint,
                    itemStyle: {
                      ...originalItemStyle,
                      opacity: 0.3
                    }
                  };
                }

                // Keep highlighted points as they are
                return dataPoint;
              }
            );
          }

          return newSeries;
        }
        return series;
      });

      // If there's a hoveredId, create overlay series for highlighted points
      if (hoveredId) {
        const highlightSeries = [];

        // Find scatter series with matching points
        option.series.forEach((series) => {
          if (series.type === "scatter" && Array.isArray(series.data)) {
            const highlightedPoints = series.data
              .filter(
                (dataPoint: { value: (number | string)[] }) =>
                  dataPoint.value && dataPoint.value[2] === hoveredId
              )
              .map((dataPoint: { value: (number | string)[]; itemStyle }) => {
                // Get original or default size
                const originalSize =
                  typeof series.symbolSize === "number" ? series.symbolSize : 8;

                // Clone the point and apply highlight styling
                return {
                  ...dataPoint,
                  symbolSize: originalSize * 1.5,
                  itemStyle: {
                    ...(dataPoint.itemStyle || {}), // Preserve original color
                    opacity: 1,
                    borderColor: "#333",
                    borderWidth: 1
                  }
                };
              });

            // If we found matching points, create an overlay series
            if (highlightedPoints.length > 0) {
              highlightSeries.push({
                type: "scatter",
                kind: "highlight",
                data: highlightedPoints,
                xAxisIndex: series.xAxisIndex || 0,
                yAxisIndex: series.yAxisIndex || 0,
                // Disable interactions to avoid duplicates
                tooltip: { show: false },
                // Ensure it doesn't trigger legend or other components
                silent: true,
                animation: false
              });
            }
          }
        });

        // Add highlight series at the end to draw on top
        option.series = [...option.series, ...highlightSeries];
      }
    }

    return option;
  }
};

export const lineHighlightDecorator: ChartDecorator = {
  decorate: function (option: EChartsOption, context: ChartContext): EChartsOption {
    // Extract hoveredId from context if available
    const hoveredId = context?.hoveredId;

    if (option.series && Array.isArray(option.series)) {
      // If there's a hoveredId, create overlay series with enhanced lines
      if (hoveredId) {
        const highlightSeries = [];

        // Find line series to highlight
        option.series.forEach((series) => {
          if (series.type === "line") {
            const seriesName = series.name?.toString() || "";
            const seriesId = (series as { id?: string }).id?.toString() || "";

            // Check if this series matches hoveredId - adjust this logic to your data structure
            const isHighlighted = seriesName === hoveredId || seriesId === hoveredId;

            if (isHighlighted) {
              // Get the original or default line width
              const originalWidth =
                series.lineStyle?.width ||
                (series.lineStyle && typeof series.lineStyle === "number"
                  ? series.lineStyle
                  : 2);

              // Clone the series for highlighted version
              const highlightedSeries = {
                ...series,
                // Enhanced line style for highlighted series
                lineStyle: {
                  ...(typeof series.lineStyle === "object" ? series.lineStyle : {}),
                  width: typeof originalWidth === "number" ? originalWidth * 3 : 6,
                  shadowBlur: 8,
                  shadowColor: "rgba(0, 0, 0, 0.3)",
                  opacity: 1
                },
                z: 10,
                // Disable tooltip to prevent duplicates
                tooltip: { show: false },
                // Don't affect legend
                silent: true
              };

              highlightSeries.push(highlightedSeries);
            } else {
              // Dim other series directly
              (series as { lineStyle }).lineStyle = {
                ...(typeof series.lineStyle === "object" ? series.lineStyle : {}),
                opacity: 0.15,
                width: 1
              };
            }
          }
        });

        // Add highlight series at the end
        if (highlightSeries.length > 0) {
          option.series = [...option.series, ...highlightSeries];
        }
      } else {
        option.series = option.series.map((series) => {
          if (series.type === "line") {
            // Restore original opacity and width if they were changed
            return {
              ...series,
              lineStyle: {
                ...(typeof series.lineStyle === "object" ? series.lineStyle : {}),
                opacity: 1,
                width: 1
              }
            };
          }
          return series;
        });
      }
    }

    return option;
  }
};
